/**
 * Utility Functions Module
 *
 * This module provides utility functions used throughout the application.
 * It includes helpers for class name management and other common operations.
 *
 * The primary utility here is the `cn` function which combines the power of
 * clsx (for conditional class names) and tailwind-merge (for resolving
 * Tailwind CSS class conflicts).
 */
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import i18next from "i18next";

/**
 * Combines multiple class values into a single className string
 *
 * This utility function combines the power of clsx and tailwind-merge to:
 * 1. Process conditional class names, arrays, objects, etc. with clsx
 * 2. Properly merge Tailwind CSS classes and handle conflicts with tailwind-merge
 *
 * Use this function whenever you need to combine multiple classes, especially
 * when working with conditional classes or component variants.
 *
 * @example
 * // Basic usage
 * cn('text-red-500', 'bg-blue-500')
 *
 * // With conditionals
 * cn('text-base', isLarge && 'text-lg')
 *
 * // With object syntax
 * cn({ 'text-red-500': isError, 'text-green-500': isSuccess })
 *
 * // Resolving conflicts (last one wins)
 * cn('text-red-500', 'text-blue-500') // -> 'text-blue-500'
 *
 * @param inputs - Any number of class values (strings, objects, arrays, etc.)
 * @returns A merged className string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatDateWithDay = (dateStr: string) => {
  if (!dateStr) return "";

  // 날짜 파싱
  let date: Date;

  if (/^\d{8}$/.test(dateStr)) {
    // YYYYMMDD 형식인 경우
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1; // 0-indexed month
    const day = parseInt(dateStr.substring(6, 8));
    date = new Date(year, month, day);
  } else {
    // YYYY-MM-DD 형식 등 다른 형식인 경우
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) {
    return dateStr; // 파싱할 수 없는 경우 원본 반환
  }

  // 다국어 요일 계산
  const dayNames = i18next.t("days", { returnObjects: true }) as string[];
  const dayName = dayNames[date.getDay()];

  // 현재 연도 확인
  const currentYear = new Date().getFullYear();
  const dateYear = date.getFullYear();

  // MM.dd (요일) 형식으로 변환 (올해가 아니면 YY.MM.dd (요일))
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");

  if (dateYear !== currentYear) {
    const yearStr = dateYear.toString().slice(2);
    return `${yearStr}.${month}.${day} (${dayName})`;
  }

  return `${month}.${day} (${dayName})`;
};



export const formatDate = (dateStr: string) => {
  if (!dateStr) return "";

  let date: Date;
  let year: string, month: string, day: string;

  if (/^\d{8}$/.test(dateStr)) {
    // YYYYMMDD 형식인 경우
    year = dateStr.substring(2, 4);
    month = dateStr.substring(4, 6);
    day = dateStr.substring(6, 8);
    date = new Date(
      parseInt(dateStr.substring(0, 4)),
      parseInt(month) - 1,
      parseInt(day),
    );
  } else {
    // YYYY-MM-DD 형식 등 다른 형식인 경우
    date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr; // 파싱할 수 없는 경우 원본 반환
    }
    year = date.getFullYear().toString().slice(2);
    month = (date.getMonth() + 1).toString().padStart(2, "0");
    day = date.getDate().toString().padStart(2, "0");
  }

  // 현재 연도 확인
  const currentYear = new Date().getFullYear();
  const dateYear = date.getFullYear();

  // 올해가 아니면 YY.MM.DD 형식으로, 올해면 MM.DD 형식으로 반환
  if (dateYear !== currentYear) {
    return `${year}.${month}.${day}`;
  }

  return `${month}.${day}`;
};

export const formatTime = (timeStr: string) => {
  if (!timeStr) return "";
  if (/^\d{4}$/.test(timeStr)) {
    const hour = timeStr.substring(0, 2);
    const minute = timeStr.substring(2, 4);
    return `${hour}:${minute}`;
  }
  return timeStr;
};


// 배경색에 따라 텍스트 색상을 결정하는 유틸리티 함수
export const getContrastTextColor = (backgroundColor: string): string => {
  // 색상 코드에서 RGB 값 추출
  const hexToRgb = (
      hex: string,
  ): { r: number; g: number; b: number } | null => {
      // # 기호 제거
      const cleanHex = hex.startsWith("#") ? hex.slice(1) : hex;

      // 3자리 hex 코드를 6자리로 변환 (예: #abc -> #aabbcc)
      const fullHex =
          cleanHex.length === 3
              ? cleanHex
                  .split("")
                  .map((c) => c + c)
                  .join("")
              : cleanHex;

      // 잘못된 hex 코드인 경우 처리
      if (!/^[0-9A-Fa-f]{6}$/.test(fullHex)) {
          return null;
      }

      // RGB 값 추출
      return {
          r: parseInt(fullHex.slice(0, 2), 16),
          g: parseInt(fullHex.slice(2, 4), 16),
          b: parseInt(fullHex.slice(4, 6), 16),
      };
  };

  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return "white"; // 기본값

  // 색상 밝기 계산 (YIQ 공식)
  // 참고: https://www.w3.org/TR/AERT/#color-contrast
  const brightness = rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114;

  // 밝기에 따라 검은색 또는 흰색 텍스트 반환 (임계값: 150)
  return brightness > 130 ? "black" : "white";
};

export const formatPlayerNames = (playerList: any[]) => {
  if (!playerList || playerList.length === 0) return "";
  
  const playerNames = playerList.map(user => {
    let name = user.playerName;
    // [ 문자가 있으면 그 이후를 모두 잘라내기
    const bracketIndex = name.indexOf('[');
    if (bracketIndex !== -1) {
      name = name.substring(0, bracketIndex);
    }
    return name;
  });
  
  if (playerNames.length === 1) {
    // 1명인 경우: 13자 이상이면 자르기
    const name = playerNames[0];
    return name.length > 13 ? name.substring(0, 13) : name;
  } else {
    // 2명인 경우
    const [firstPlayer, secondPlayer] = playerNames;
    const combinedLength = firstPlayer.length + secondPlayer.length + 1; // +1은 "/" 때문
    
    if (combinedLength > 13) {
      // 합쳐서 13자 이상이면 각각 6자로 자르기
      const truncatedFirst = firstPlayer.substring(0, 6);
      const truncatedSecond = secondPlayer.substring(0, 6);
      return `${truncatedFirst}/${truncatedSecond}`;
    } else {
      // 13자 이하면 그대로
      return playerNames.join("/");
    }
  }
};

// 선수 목록에서 팀명(teamName)을 추출하여 표시
export const formatTeamNames = (playerList: any[], fallbackName?: string) => {
  if (!playerList || playerList.length === 0) return fallbackName || "";
  
  // 각 선수의 teamName 추출 (빈 값 제외)
  const teamNames = playerList
    .map(user => user.teamName?.trim())
    .filter((name): name is string => !!name);
  
  if (teamNames.length === 0) return fallbackName || "";
  
  // 중복 제거
  const uniqueTeamNames = [...new Set(teamNames)];
  
  return uniqueTeamNames.join("/");
};

export const formatGameName = (gameName: string, locale: string = "en") => {
  if (!gameName) return "";
  let name = gameName.replace(/^개인전\s*/, "");
  if (locale !== "ko") {
    name = name.replace(/강$/, "R");
    if (name.includes("단식")) {
      name = name.replace(/단식/g, "");
    }
    if (name.includes("준결승")) {
      name = name.replace(/준결승/g, "SF");
    }
    if (name.includes("결승")) {
      name = name.replace(/결승/g, "Finals");
    }
  }
  return name;
};
// 색상이 흰색인지 확인하는 함수
export const isWhiteColor = (color: string): boolean => {
  // # 기호 제거
  const cleanHex = color.startsWith("#") ? color.slice(1) : color;

  // 대략적인 흰색 판단 (완전한 흰색 또는 매우 밝은 색상)
  if (cleanHex.toUpperCase() === "FFFFFF") return true;

  // RGB 값으로 변환하여 판단
  const hexToRgb = (
      hex: string,
  ): { r: number; g: number; b: number } | null => {
      // 3자리 hex 코드를 6자리로 변환
      const fullHex =
          hex.length === 3
              ? hex
                  .split("")
                  .map((c) => c + c)
                  .join("")
              : hex;

      if (!/^[0-9A-Fa-f]{6}$/.test(fullHex)) {
          return null;
      }

      return {
          r: parseInt(fullHex.slice(0, 2), 16),
          g: parseInt(fullHex.slice(2, 4), 16),
          b: parseInt(fullHex.slice(4, 6), 16),
      };
  };

  const rgb = hexToRgb(cleanHex);
  if (!rgb) return false;

  // RGB 값이 모두 매우 높으면 (240 이상) 흰색으로 간주
  return rgb.r >= 240 && rgb.g >= 240 && rgb.b >= 240;
};

export function getGameNameConvert(gameName?: string) {
  if (!gameName) return "";

  return gameName
    .replace(/남단/g, "남자단식")
    .replace(/남복/g, "남자복식")
    .replace(/여복/g, "여자복식")
    .replace(/여단/g, "여자단식")
    .replace(/풀토너먼트/g, "")
    .replace(/개인전/g, "")
    .replace(/혼복/g, "혼합복식");
}